import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { IconDownload } from '@tabler/icons-react'
import pluralize from 'pluralize'

import { Button } from '@/components/buttons/Button'
import { useBackoffPoll } from '@/utils/hooks/useBackoffPoll'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { Loader } from '@/components/loaders/Loader'
import NoData from '@/components/NoData'
import { Pill } from '@/components/pills/Pill'
import { Progress } from '@/components/progress/Progress'
import { Table } from '@/components/tables/Table/Table'
import { EXPORT_STATUS } from '@/utils/constants'
import {
  downloadAssetExport,
  fetchAssetExport,
  isTerminal,
} from '@/reducers/callAssetExports/callAssetExports.actions'

import './CallAssetExportDetail.scss'

// Progress is a courtesy for whoever stays on the page, not the delivery
// mechanism -- the notification and the email are. So the poll is slow enough
// to be cheap and fast enough to feel live, and nothing depends on it.
//
// Responsive while someone has just started a job, then easing off: a fixed 5s
// poll on a tab left open for an hour is 720 requests. Capped at 15s rather
// than stretching further, because the assumption behind a long backoff does
// not hold here. Elsewhere "still going after five minutes" means nobody is
// watching; for an export it means a large selection, which is exactly when
// someone sits and watches the bar. At a minute the bar moved in visible
// lurches. 15s is four updates a minute and still ~240 requests an hour
// against a fixed 5s poll's 720.
const POLL_SCHEDULE = [
  { every: 5000, until: 30000 },
  { every: 10000, until: 120000 },
  { every: 15000, until: Infinity },
]

// The same words and colours as the Exports list's Status column, so the row
// that was clicked and the drawer it opened agree.
export const STATUS_PILL = {
  [EXPORT_STATUS.queued]: { label: 'Queued' },
  [EXPORT_STATUS.running]: { label: 'Preparing', brand: true },
  [EXPORT_STATUS.succeeded]: { label: 'Ready', success: true },
  [EXPORT_STATUS.partial]: { label: 'Ready', success: true },
  [EXPORT_STATUS.failed]: { label: 'Failed', critical: true },
  [EXPORT_STATUS.expired]: { label: 'Expired' },
}

/**
 * The body of the export drawer on the Exports page, for the export in the
 * route (`/exports/:id`).
 * @import import { CallAssetExportDetail } from '@/views/Calls/CallAssetExportDetail'
 * @param {object} props
 * @param {Function} props.onClose Closes the drawer
 */
export const CallAssetExportDetail = ({ onClose }) => {
  const { id } = useParams()
  const dispatch = useDispatch()
  const { currentExport, exportNotFound, exportForbidden } = useSelector(
    (state) => state.callAssetExports
  )
  // Matched against the route id for the same reason `loaded` is: a 404 that
  // lands after the user has moved to another export belongs to the one they
  // left, and showing it here would strand this drawer on the empty state.
  const notFound = exportNotFound != null && String(exportNotFound) === String(id)
  const forbidden = exportForbidden != null && String(exportForbidden) === String(id)
  // The slice still holds the previous export for a moment after moving from
  // one job to another. Showing it would put another export's counts under
  // this id, so it reads as not-yet-loaded until the fetch catches up.
  const loaded = currentExport && String(currentExport.id) === String(id) ? currentExport : null

  useEffect(() => {
    dispatch(fetchAssetExport(id))
  }, [id])

  // Stops on any terminal state rather than on a success check, so a state
  // nobody anticipated leaves the drawer polling instead of silently claiming
  // the export finished. Nothing to poll for either once the id is known not
  // to exist, or a stale link would keep asking for a dead export.
  //
  // Read from `loaded`, so only THIS id's status counts. The previous
  // export's terminal status carrying across would stop the poll on a drawer
  // that has not loaded once, leaving a transient failure spinning for ever.
  const settled = notFound || forbidden || isTerminal(loaded?.status)

  useBackoffPoll(() => dispatch(fetchAssetExport(id, { quiet: true })), {
    enabled: !settled,
    schedule: POLL_SCHEDULE,
  })

  // Only when the fetch actually said 404. Any other failure leaves the loader
  // up and the poll running, which is the recoverable behaviour: the next tick
  // asks again. The flash this avoids is the effect that starts the fetch
  // running after paint, so "no export and not loading" is briefly true on the
  // first render for every id.
  if (notFound) {
    return (
      <div className="export-detail">
        <NoData
          headerText="This export could not be found"
          text="It may have expired, or belong to an organization you do not have access to."
        />
      </div>
    )
  }

  if (forbidden) {
    return (
      <div className="export-detail">
        <NoData
          headerText="You cannot open this export"
          text="It belongs to an organization your account does not have access to."
        />
      </div>
    )
  }

  if (!loaded) {
    return <Loader centered />
  }

  const {
    status,
    call_count: callCount,
    calls_written: callsWritten,
    total_bytes: totalBytes,
    bytes_written: bytesWritten,
    size_label: sizeLabel,
    expires_on: expiresOn,
    asset_summary: assetSummary,
    error,
    skipped_count: skippedCount,
    can_download: canDownload = true,
    created_at: createdAt,
    created_by: createdBy,
  } = loaded

  const finished = isTerminal(status)
  const ready = status === EXPORT_STATUS.succeeded || status === EXPORT_STATUS.partial
  // Two different things, and conflating them is what would put a button on
  // screen that 403s: whether the archive exists, and whether this viewer may
  // fetch it. A Balto user looking at an unredacted organization with no open
  // support-access window sees the job and not the bytes.
  const downloadable = ready && canDownload

  // Measured in bytes, reported in calls. A bar counting calls would lurch,
  // because calls vary enough in length that an average one is not a
  // meaningful unit of progress -- but calls are what the user selected, so
  // that is what the label says.
  //
  // Floored and held below 100 because this figure is only ever shown while
  // the job is still running: rounding put a 10k export at "100%" next to
  // "9,975 of 10,000 calls", and bytes can pass the total outright, since
  // `total_bytes` covers the assets while the archive also carries a
  // manifest. 100% is a thing the user should only read once it is true.
  const percent = totalBytes ? Math.min(99, Math.floor((bytesWritten / totalBytes) * 100)) : 0
  const sizeKnown = Boolean(totalBytes)
  const statusPill = STATUS_PILL[status] || { label: status }

  // Started is left out when the job carries no timestamp, and Created by when
  // the account that asked has since been deleted -- an export outlives its
  // requester by up to a week. Size is left out while it is still being
  // measured rather than holding a placeholder, and is a dash once the export
  // has finished without one, so it never promises a number that is not
  // coming.
  const details = [
    createdAt && { label: 'Started', value: new Date(createdAt).toLocaleString() },
    createdBy && { label: 'Created by', value: createdBy },
    { label: 'Calls', value: (callCount ?? 0).toLocaleString() },
    { label: 'Includes', value: assetSummary || '—' },
    (sizeLabel || finished) && { label: 'Size', value: sizeLabel || '—' },
  ].filter(Boolean)

  return (
    <div className="export-detail">
      <section className="export-detail__status">
        <div className="flex-space-between flex-align-center">
          <Pill
            brand={statusPill.brand}
            success={statusPill.success}
            critical={statusPill.critical}
          >
            {statusPill.label}
          </Pill>
          {!finished && sizeKnown && <span className="muted-text">{percent}%</span>}
          {downloadable && expiresOn && (
            <span className="muted-text">Available until {expiresOn}</span>
          )}
        </div>

        {!finished && (
          <>
            <Progress value={percent} indeterminate={!sizeKnown} />
            <p className="muted-text">
              {sizeKnown
                ? `${(callsWritten ?? 0).toLocaleString()} of ${(
                    callCount ?? 0
                  ).toLocaleString()} ${pluralize('call', callCount ?? 0)}`
                : 'Working out how much there is to collect…'}
            </p>
            {/* The drawer is not the delivery mechanism, and a long export does
                not need anybody watching it. Say so, so nobody waits. */}
            <p className="muted-text">
              You can leave this page. We&apos;ll email you when it&apos;s ready.
            </p>
          </>
        )}

        {status === EXPORT_STATUS.failed && (
          // The reason carries its own guidance -- whether a retry is worth it
          // at all, and where to start one.
          <ErrorMessage
            content={error || 'The export stopped before it finished. Nothing was saved.'}
          />
        )}

        {status === EXPORT_STATUS.expired && (
          <p className="muted-text">
            Exports stay available for a week. Start a new one from Explorer to download these calls
            again.
          </p>
        )}
      </section>

      <section>
        <h5 className="export-detail__heading">Details</h5>
        {/* The same label-and-value table as the call page's Details tab. */}
        <Table compact celled>
          <tbody>
            {details.map(({ label, value }) => (
              <tr key={label}>
                <td>
                  <strong>{label}</strong>
                </td>
                <td>
                  <span className="muted-text">{value}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </section>

      {/* A shortfall is normal rather than an error: audio expires on a
          per-organization schedule and some calls never had it saved. Shown
          here as well as in the archive manifest so it is not a surprise
          discovered inside a zip. */}
      {downloadable && skippedCount > 0 && (
        <p className="muted-text">
          {skippedCount.toLocaleString()} {pluralize('call', skippedCount)} had nothing to collect
          and {pluralize('is', skippedCount)} listed in the archive manifest.
        </p>
      )}

      {ready && !canDownload && (
        <p className="muted-text">
          This export is ready, but downloading it needs an open support-access window from the
          organization that owns the calls.
        </p>
      )}

      {downloadable && (
        <div className="drawer-footer flex-align-center flex-end small-gap">
          <Button onClick={onClose}>Close</Button>
          <Button
            brand
            icon={<IconDownload />}
            onClick={() => dispatch(downloadAssetExport(id))}
            data-testid="export-download-button"
          >
            Download
          </Button>
        </div>
      )}
    </div>
  )
}
